import Modal from 'react-modal';
import "../../styles/AdminPage.css";
import Button, { BUTTON_TYPES } from '../Button';
import TextInput from '../inputs/TextInput';
import PropTypes from 'prop-types';
import React, { useEffect, useState, useRef } from 'react';
import DateInput from '../inputs/DateInput';
import Dropdown from '../inputs/Dropdown';
import { FaTimes } from "react-icons/fa";
import CheckboxGroupScrollable from '../inputs/CheckboxGroupScrollable';
import { duplicateEvent } from '../../api/admin/AdminDashboardAPI';
import _ from 'lodash';
import { DateTimeFormatter, LocalDateTime } from '@js-joda/core';
import toast from 'react-hot-toast';
import { useAdmin } from '../../pages/admin/AdminContext';
import { generateRecurrenceSchedule } from '../../api/admin/RecurrenceFormatter';
import RecurrenceSelector from '../inputs/RecurrenceSelector';

Modal.setAppElement('#root');


// TODO: check if startDate is before endDate

const RescheduleModal = ({ isOpen, onClose, event, initialFormData, onFormDataChange, addNewEvent, eventTypes, updateEventWithRecurrence }) => {
    const [name, setName] = useState(event.eventName);
    const [startDate, setStartDate] = useState(event.startDate);
    const [endDate, setEndDate] = useState(event.endDate);
    const [selectedRecurrence, setSelectedRecurrence] = useState(event?.recurrence?.frequency || "None"); // TODO
    const [location, setLocation] = useState(event.location);
    const [selectedCommittee, setSelectedCommittee] = useState(event.committee.committeeName);
    // const [selectedEventTypes, setSelectedEventTypes] = useState(event.eventTypes); // not needed?
    const [selectedEventTypeIDs, setSelectedEventTypeIDs] = useState(() =>
        event?.eventTypes ? event.eventTypes.map(type => type.id) : []
    );

    const [recurrOption, setRecurrOption] = useState(event?.recurrence || null);

    const { committees } = useAdmin();

    const newCommittee = committees.find(c => c.committeeName === selectedCommittee) || { committeeName: selectedCommittee };
    const newEventTypes = eventTypes.filter(et => selectedEventTypeIDs.includes(et.id));

    const updatedEvent = {
        eventName: name,
        startDate: startDate,
        endDate: endDate,
        location: location,
        committee: newCommittee,
        eventTypes: newEventTypes,
    };

    const didInit = useRef(false);
    const onFormDataChangeRef = useRef(onFormDataChange);
    useEffect(() => {
        onFormDataChangeRef.current = onFormDataChange;
    }, [onFormDataChange]);

    useEffect(() => {
        requestAnimationFrame(() => {
            const checkboxes = document.querySelectorAll('input[name="eventTypesGroup"]');
            checkboxes.forEach((checkbox) => {
                const value = Number(checkbox.value);
                checkbox.checked = selectedEventTypeIDs.includes(value);
            });
        });
    }, [selectedEventTypeIDs]);

    useEffect(() => {
        if (onFormDataChangeRef.current) {
            onFormDataChangeRef.current({
                name,
                startDate,
                endDate,
                selectedRecurrence,
                location,
                selectedCommittee,
                selectedEventTypeIDs
            });
        }
    }, [name, startDate, endDate, selectedRecurrence, location, selectedCommittee, selectedEventTypeIDs]);

    const handleEventTypeSelect = (evt) => {
        const changedValue = Number(evt.target.value);
        const isChecked = evt.target.checked;
        if (isChecked) {
            setSelectedEventTypeIDs(prev => [...prev, changedValue]);
        } else {
            setSelectedEventTypeIDs(prev => prev.filter((id) => id !== changedValue));
        }
    };

    const committeeOptions = React.useMemo(() => [
        { value: 'None', label: 'None' },
        ...committees.map((committee) => ({
            value: committee.committeeName,
            label: committee.committeeName
        }))
    ], [committees]);

    const handleCommitteeChange = (e) => {
        setSelectedCommittee(e.target.value);
    };

    const recurrenceOptions = [
        { value: 'None', label: 'No Recurrence' },
        { value: 'DAILY', label: 'Daily' },
        { value: 'WEEKLY', label: 'Weekly' },
        { value: 'MONTHLY', label: 'Monthly' }
    ];
    const handleRecurrenceChange = (e) => {
        setSelectedRecurrence(e);
    };

    const handleReset = () => {
        setName(event.eventName);
        setStartDate(event.startDate);
        setEndDate(event.endDate);
        setSelectedRecurrence(event?.recurrence?.frequency || "None");
        setLocation(event.location);
        setSelectedCommittee(event.committee.committeeName);

        setSelectedEventTypeIDs(() =>
            event?.eventTypes ? event.eventTypes.map(type => type.id) : []
        );
    };

    const submitEvent = async () => {
        const isUnchanged = _.isEqual(event, updatedEvent);

        if (isUnchanged) {
            toast.error("No data changed");
            return;
        }

        const isCorrectDate = LocalDateTime.parse(updatedEvent.startDate, DateTimeFormatter.ISO_DATE_TIME)
            .isBefore(LocalDateTime.parse(updatedEvent.endDate, DateTimeFormatter.ISO_DATE_TIME));

        if (!isCorrectDate) {
            toast.error("Start Date & Time must be\n before End Date & Time");
            return;
        }

        if (updatedEvent.eventName.length > 80) {
            toast.error("Event Name is too long");
            return;
        }

        if (selectedRecurrence !== "None") {
            if (!recurrOption?.endDate) {
                toast.error("Please select an end date for the recurrence.");
                return;
            }
            const eventEndDay = updatedEvent.endDate.slice(0, 10);
            if (recurrOption.endDate <= eventEndDay) {
                toast.error("Recurrence end date cannot be before the event end date.");
                return;
            }
            if (selectedRecurrence === "WEEKLY" &&
                (!Array.isArray(recurrOption.weekDays) || !recurrOption.weekDays.length)) {
                toast.error("Select at least one weekday for a weekly recurrence.");
                return;
            }
            if (selectedRecurrence === "MONTHLY" &&
                !Number.isInteger(recurrOption.dateOfMonth)) {
                toast.error("Please pick a day of the month for a monthly recurrence.");
                return;
            }
        }

        try {
            toast.loading("Duplicating Event...");
            setTimeout(async () => {
                console.log("Payload to duplicate:", updatedEvent);
                let jsonBody;
                if (recurrOption === null) {
                    jsonBody = { event: updatedEvent };
                } else {
                    recurrOption.startDate = startDate;
                    console.log(recurrOption);
                    const recurrDates = generateRecurrenceSchedule(recurrOption).occurrences;
                    jsonBody = {
                        event: updatedEvent,
                        recurrence: recurrOption,
                        dates: recurrDates
                    };
                }
                const response = await duplicateEvent(jsonBody);
                if (response.ok) {
                    const returnedEvent = await response.json();
                    console.log(returnedEvent);
                    toast.dismiss();
                    didInit.current = false;
                    if (recurrOption === null) {
                        addNewEvent(returnedEvent);
                        toast.success("Event duplicated!");
                    } else {
                        updateEventWithRecurrence();
                        toast.success("Event duplicated and added recurrences!");
                    }
                    onClose();
                } else {
                    toast.dismiss();
                    console.log("Error duplicating event. Status:", response.status);
                    toast.error("Event failed to duplicate. Status: " + response.status);
                }
            }, 300);
        } catch (error) {
            toast.dismiss();
            console.error("Error submitting event:", error);
            toast.error("Something went wrong. Please check your connection and try again.");
        }

    };

    return (
        <Modal
            isOpen={isOpen}
            onRequestClose={onClose}
            contentLabel="Reschedule Modal"
            className="custom-modal-content member-filter-modal"
            overlayClassName="custom-modal-overlay"
        >
            <>
                <div className='d-flex flex-row justify-content-between align-items-center mb-3'>
                    <h2 className="text-start mb-0">Duplicate Event</h2>
                    <div className='d-flex flex-row justify-content-end align-items-center me-1'>
                        <button className="close-btn" onClick={onClose}>
                            <FaTimes />
                        </button>
                    </div>
                </div>
                <div className="d-flex flex-row justify-content-between mb-1">
                    <div className="form-field w-50 me-2">
                        <TextInput
                            id="eventNameEdit"
                            placeholder="Event Name"
                            className="form-input"
                            charLimit={200}
                            label="Event Name"
                            value={name}
                            setValue={(e) => setName(e.target.value)}
                            required
                        />
                    </div>
                    <div className="form-field w-50">
                        <TextInput
                            id="eventLocationEdit"
                            placeholder="Location"
                            className="form-input"
                            charLimit={300}
                            label="Location"
                            value={location}
                            setValue={(e) => setLocation(e.target.value)}
                        />
                    </div>
                </div>
                <div className="d-flex flex-row justify-content-between mb-1">
                    <div className="form-field w-50 me-2">
                        <DateInput
                            id="eventStartDateEdit"
                            className="form-input"
                            label="Start Date"
                            value={startDate}
                            setValue={(e) => setStartDate(e.target.value)}
                            required
                        />
                    </div>
                    <div className="form-field w-50">
                        <Dropdown
                            id="committee-dropdown"
                            name="committees"
                            options={committeeOptions}
                            onChange={handleCommitteeChange}
                            className="form-input"
                            label="Committee"
                            value={selectedCommittee}
                            required
                        />
                    </div>
                </div>
                <div className="d-flex flex-row justify-content-between mb-1">
                    <div className="form-field w-50 me-2">
                        <DateInput
                            id="eventEndDateEdit"
                            className="form-input"
                            label="End Date"
                            value={endDate}
                            setValue={(e) => setEndDate(e.target.value)}
                            required
                        />
                    </div>
                    <div className="form-field w-50">
                        <Dropdown
                            id="recurrence-dropdown"
                            name="recurrence"
                            options={recurrenceOptions}
                            onChange={(e) => handleRecurrenceChange(e.target.value)}
                            className="form-input"
                            label="Recurrence"
                            value={selectedRecurrence}
                            required
                        />
                    </div>

                </div>
                <div className="d-flex flex-row justify-content-between mb-1">
                    <div className="form-field w-50 me-2">
                        <CheckboxGroupScrollable
                            id="event-types"
                            name="eventTypesGroup"
                            label="Event Type(s)"
                            options={eventTypes.map((et) => ({
                                value: et.id,
                                label: et.eventType,
                            }))}
                            selectedValues={selectedEventTypeIDs}
                            onChange={handleEventTypeSelect}
                            onSelectAll={() => setSelectedEventTypeIDs(eventTypes.map((et) => et.id))}
                            onUnselectAll={() => setSelectedEventTypeIDs([])}
                            taller={true}
                        />
                    </div>
                    <div className="form-field w-50">
                        <RecurrenceSelector
                            selectedRecurrence={selectedRecurrence}
                            initialValue={recurrOption}
                            onChange={(c) => { setRecurrOption(c) }}
                        />
                    </div>
                </div>
                <div className='d-flex flex-row justify-content-center align-items-center two-button-container'>
                    <div className='me-3'>
                        <Button
                            id="reset-button"
                            type={BUTTON_TYPES.outline}
                            buttonText="Reset"
                            action={handleReset}
                        />
                    </div>
                    {/* TODO: make this a call to backend /api/events/modify with eventId attributes */}
                    <Button
                        id="filter-button-modal"
                        type={BUTTON_TYPES.primary}
                        buttonText="Duplicate"
                        action={submitEvent}
                    />
                </div>
            </>
        </Modal>
    );
};

RescheduleModal.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    event: PropTypes.object,
    initialFormData: PropTypes.object,
    onFormDataChange: PropTypes.func,
};

export default RescheduleModal;
